import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { orderService } from '../../services/orderService';
import { adminService } from '../../services/adminService';

interface AdminStats {
    total_orders: number;
    pending_orders: number;
    total_revenue: number;
    today_orders: number;
    pending_registrations: number;
}

const AdminDashboard: React.FC = () => {
    const { user } = useAuth();
    const [stats, setStats] = useState<AdminStats | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadStats();
    }, []);

    const loadStats = async () => {
        try {
            setLoading(true);
            const [orderStatsResponse, pendingRegistrationsResponse] = await Promise.all([
                orderService.getOrderStats(),
                adminService.getPendingRegistrationsCount()
            ]);

            setStats({
                ...orderStatsResponse.stats,
                pending_registrations: pendingRegistrationsResponse.count
            });
        } catch (error) {
            console.error('Erreur lors du chargement des statistiques:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatPrice = (price: number) => {
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'EUR'
        }).format(price);
    };

    if (user?.role !== 'admin') {
        return (
            <div className="min-h-screen bg-eldai-gray-light flex items-center justify-center">
                <div className="bg-white p-8 rounded-lg shadow-md text-center">
                    <h1 className="text-2xl font-bold text-red-600 mb-4">Accès refusé</h1>
                    <p className="text-eldai-gray">Vous n'avez pas les permissions pour accéder à cette page.</p>
                    <Link to="/" className="btn btn-primary mt-4 inline-block">
                        Retour à l'accueil
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-eldai-gray-light">
            <div className="container mx-auto px-4 py-8">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-eldai-blue mb-2">
                        Tableau de bord administrateur
                    </h1>
                    <p className="text-eldai-gray">
                        Bienvenue, {user?.first_name}. Gérez votre boutique en ligne Eldaï.
                    </p>
                </div>

                {/* Quick Stats */}
                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
                        {[...Array(5)].map((_, i) => (
                            <div key={i} className="bg-white p-6 rounded-lg shadow-md">
                                <div className="animate-pulse">
                                    <div className="h-4 bg-eldai-gray-light rounded w-3/4 mb-2"></div>
                                    <div className="h-8 bg-eldai-gray-light rounded w-1/2"></div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : stats && (
                    <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
                        <div className="bg-white p-6 rounded-lg shadow-md">
                            <div className="flex items-center">
                                <div className="text-3xl mr-4">⏳</div>
                                <div>
                                    <p className="text-sm text-eldai-gray">Commandes en attente</p>
                                    <p className="text-2xl font-bold text-yellow-600">{stats.pending_orders}</p>
                                </div>
                            </div>
                        </div>
                        <div className="bg-white p-6 rounded-lg shadow-md">
                            <div className="flex items-center">
                                <div className="text-3xl mr-4">👥</div>
                                <div>
                                    <p className="text-sm text-eldai-gray">Inscriptions en attente</p>
                                    <p className="text-2xl font-bold text-orange-600">{stats.pending_registrations}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Action Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* Product Management */}
                    <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
                        <div className="flex items-center mb-4">
                            <div className="text-4xl mr-4">🏷️</div>
                            <div>
                                <h2 className="text-xl font-bold text-eldai-blue">Gestion des produits</h2>
                                <p className="text-eldai-gray text-sm">Importez et gérez votre catalogue</p>
                            </div>
                        </div>
                        <div className="space-y-3">
                            <Link
                                to="/admin/products/import"
                                className="block w-full bg-gradient-to-r from-eldai-yellow to-eldai-accent text-eldai-blue py-3 px-4 rounded-lg font-semibold hover:from-eldai-blue hover:to-eldai-light-blue hover:text-white transition-all duration-300 transform hover:scale-105 hover:shadow-lg text-center"
                            >
                                📥 Importer CSV
                            </Link>
                            <Link
                                to="/admin/products"
                                className="block w-full border-2 border-eldai-blue text-eldai-blue py-3 px-4 rounded-lg font-semibold hover:bg-gradient-to-r hover:from-eldai-blue hover:to-eldai-light-blue hover:text-white transition-all duration-300 transform hover:scale-105 hover:shadow-lg text-center"
                            >
                                📋 Gérer les produits
                            </Link>
                            <Link
                                to="/admin/products/featured"
                                className="block w-full border-2 border-eldai-blue text-eldai-blue py-3 px-4 rounded-lg font-semibold hover:bg-gradient-to-r hover:from-eldai-blue hover:to-eldai-light-blue hover:text-white transition-all duration-300 transform hover:scale-105 hover:shadow-lg text-center"
                            >
                                ⭐ Produits en vedette
                            </Link>
                            <Link
                                to="/admin/images"
                                className="block w-full border-2 border-eldai-blue text-eldai-blue py-3 px-4 rounded-lg font-semibold hover:bg-gradient-to-r hover:from-eldai-blue hover:to-eldai-light-blue hover:text-white transition-all duration-300 transform hover:scale-105 hover:shadow-lg text-center"
                            >
                                📸 Gérer les images
                            </Link>
                            <Link
                                to="/admin/carousel"
                                className="block w-full border-2 border-eldai-blue text-eldai-blue py-3 px-4 rounded-lg font-semibold hover:bg-gradient-to-r hover:from-eldai-blue hover:to-eldai-light-blue hover:text-white transition-all duration-300 transform hover:scale-105 hover:shadow-lg text-center"
                            >
                                🎠 Gérer le carousel
                            </Link>
                            <Link
                                to="/admin/categories"
                                className="block w-full border-2 border-eldai-blue text-eldai-blue py-3 px-4 rounded-lg font-semibold hover:bg-gradient-to-r hover:from-eldai-blue hover:to-eldai-light-blue hover:text-white transition-all duration-300 transform hover:scale-105 hover:shadow-lg text-center"
                            >
                                🏷️ Gérer les images des catégories
                            </Link>
                        </div>
                    </div>

                    {/* Order Management */}
                    <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
                        <div className="flex items-center mb-4">
                            <div className="text-4xl mr-4">📦</div>
                            <div>
                                <h2 className="text-xl font-bold text-eldai-blue">Gestion des commandes</h2>
                                <p className="text-eldai-gray text-sm">Suivez et traitez les commandes</p>
                            </div>
                        </div>
                        <div className="space-y-3">
                            <Link
                                to="/admin/orders"
                                className="block w-full bg-gradient-to-r from-eldai-yellow to-eldai-accent text-eldai-blue py-3 px-4 rounded-lg font-semibold hover:from-eldai-blue hover:to-eldai-light-blue hover:text-white transition-all duration-300 transform hover:scale-105 hover:shadow-lg text-center"
                            >
                                📋 Toutes les commandes
                            </Link>
                            <Link
                                to="/admin/orders?status=pending"
                                className="block w-full border-2 border-eldai-blue text-eldai-blue py-3 px-4 rounded-lg font-semibold hover:bg-gradient-to-r hover:from-eldai-blue hover:to-eldai-light-blue hover:text-white transition-all duration-300 transform hover:scale-105 hover:shadow-lg text-center"
                            >
                                ⏳ Commandes en attente
                            </Link>
                        </div>
                    </div>

                    {/* Users Management */}
                    <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
                        <div className="flex items-center mb-4">
                            <div className="text-4xl mr-4">👥</div>
                            <div>
                                <h2 className="text-xl font-bold text-eldai-blue">Gestion des utilisateurs</h2>
                                <p className="text-eldai-gray text-sm">Gérez les comptes clients</p>
                            </div>
                        </div>
                        <div className="space-y-3">
                            <Link
                                to="/admin/users"
                                className="block w-full bg-gradient-to-r from-eldai-yellow to-eldai-accent text-eldai-blue py-3 px-4 rounded-lg font-semibold hover:from-eldai-blue hover:to-eldai-light-blue hover:text-white transition-all duration-300 transform hover:scale-105 hover:shadow-lg text-center"
                            >
                                👥 Tous les utilisateurs
                            </Link>
                            <button
                                className="w-full border-2 border-eldai-blue text-eldai-blue py-2 px-4 rounded-lg font-semibold hover:bg-eldai-blue hover:text-white transition-colors"
                                disabled
                                title="Fonctionnalité en développement"
                            >
                                ➕ Ajouter un utilisateur
                            </button>
                        </div>
                    </div>
                </div>

                {/* Quick Actions */}
                <div className="mt-8 bg-white p-6 rounded-lg shadow-md d-none">
                    <h2 className="text-xl font-bold text-eldai-blue mb-4">Actions rapides</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Link
                            to="/catalog"
                            className="flex items-center p-4 border border-eldai-gray rounded-lg hover:bg-eldai-gray-light transition-colors"
                        >
                            <span className="text-2xl mr-3">👁️</span>
                            <div>
                                <p className="font-semibold text-eldai-blue">Voir le site</p>
                                <p className="text-sm text-eldai-gray">Vue client du catalogue</p>
                            </div>
                        </Link>

                        <button
                            onClick={loadStats}
                            className="flex items-center p-4 border border-eldai-gray rounded-lg hover:bg-eldai-gray-light transition-colors"
                        >
                            <span className="text-2xl mr-3">🔄</span>
                            <div>
                                <p className="font-semibold text-eldai-blue">Actualiser</p>
                                <p className="text-sm text-eldai-gray">Recharger les statistiques</p>
                            </div>
                        </button>

                        <Link
                            to="/admin/settings"
                            className="flex items-center p-4 border border-eldai-gray rounded-lg hover:bg-eldai-gray-light transition-colors"
                        >
                            <span className="text-2xl mr-3">⚙️</span>
                            <div>
                                <p className="font-semibold text-eldai-blue">Paramètres</p>
                                <p className="text-sm text-eldai-gray">Configuration du site</p>
                            </div>
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminDashboard;
